<template>
    <div v-if="isReady">
        <div class="border rounded-4 px-24 py-16 my-12">
            <div class="flex items-center">
                <div :id="'edit-group-drag-' + this.contract.contract_id" class="text-primary-20 cursor-pointer trans-ease pointer-events-auto draggable inline-block mr-5">
                    <svg-icon type="drag-indicator"/>
                </div>
                <div class="w-64 h-64">
                    <img :src="cachedContractLogo" :alt="`logo-${contract.name}`">
                </div>
                <div class="flex flex-1 items-center truncate ml-24 mr-8">
                    <div class="flex items-center w-full">
                        <p class="text-heading-2 font-semibold mr-16 text-wrap">{{ contract.name + getAlias(contract) }}</p>
                        <tag
                            disabled
                            :display="checkExpiryDate ? 'danger' : 'success'"
                            no-icon
                        >
                            {{ checkExpiryDate ? $t('general.expired') : $t('general.active') }}
                        </tag>
                    </div>
                    <p>{{ contract.description }}</p>
                </div>
                <div class="flex">
                    <simple-button @click="viewingDetails = true">
                        {{ $t('multi-posting.contracts.index.details') }}
                    </simple-button>

                    <simple-button
                        class="ml-12"
                        display="primary"
                        look="primary"
                        @click="addToCampaign"
                    >
                        {{ $t('multi-posting.contracts.index.add_to_campaign') }}
                    </simple-button>
                </div>
            </div>
            <p class="w-full" v-if="checkExpiryDate">{{ $t('multi-posting.contracts.expired', {contract: contract.name}) }}</p>
        </div>

        <popup-modal
            v-if="viewingDetails"
            custom-classes="w-768"
            :text-center="false"
            @close="viewingDetails = false"
        >
            <ContractDetails :contract="contract" @contract-removed="contractRemoved" />
        </popup-modal>
    </div>

</template>

<script>
import SimpleButton from "../ui/SimpleButton.vue";
import PopupModal from "../ui/PopupModal.vue";
import SelectDropdown from "../forms/SelectDropdown.vue";
import MultiPostingDynamicContractForm from "./MultiPostingDynamicContractForm.vue";
import ContractDetails from "./ContractDetails.vue";
import { mapActions } from "pinia";
import {useMultiPostingStore} from '../../state/modules/multiPosting';
import Tag from "../ui/Tag.vue";
import SvgIcon from '../ui/SvgIcon.vue';
import Loading from '../ui/Loading.vue';

export default {
    name: 'ContractListItem',
    components: {
        Tag,
        ContractDetails,
        MultiPostingDynamicContractForm,
        SelectDropdown,
        PopupModal,
        SimpleButton,
        SvgIcon,
        Loading,
    },
    props: {
        contract: { type: Object, required: true },
    },
    data() {
        return {
            viewingDetails: false,
            cachedContractLogo: '',
            loadedContractLogo: false,
        }
    },
    computed: {
        contractLogo() {
            return this.contract.logo_square_url || '';
        },
        checkExpiryDate() {
            if (this.contract.expiryDate && this.contract.expiryDate !== 'Not specified') {
                const expiry = new Date(this.contract.expiryDate);
                const today = new Date();
                return expiry < today;
            }
            return false;
        }
    },
    methods: {
        ...mapActions(useMultiPostingStore, ['addSelectedItems']),
        isReady() {
            return this.loadedContractLogo;
        },
        addToCampaign() {
            this.addSelectedItems([{
                id: this.contract.product_id,
                channel_id: this.contract.channel_id,
                contract_id: this.contract.contract_id,
                name: `${this.$t('multi-posting.campaigns.form.my_contract')}: ${this.contract.name}`,
                duration: {
                    period: this.contract.postingDurationDays
                },
                price: {
                    amount: this.contract.credits,
                    currency: 'credits'
                },
                type: 'contract',
                logo_square_url: this.contract.logo_square_url,
            }]);
            this.$emit('change-tab', {tab: 'Campaigns', params: 'create'});
        },
        contractRemoved() {
            this.viewingDetails = false;
            this.$emit('contract-removed', this.contract.contract_id);
        },
        getAlias(contract) {
            return contract && contract.alias ? ` (${contract.alias})` : '';
        },
        cacheImage(url) {
            this.loadedContractLogo = false;
            return new Promise((resolve, reject) => {
                const cachedImage = sessionStorage.getItem(url);
                if (cachedImage) {
                    resolve(cachedImage);
                } else {
                    const img = new Image();
                    img.onload = () => {
                        sessionStorage.setItem(url, url);
                        resolve(url);
                    };
                    img.onerror = reject;
                    img.src = url;
                }
            })
            .then(dataURL => {
                this.cachedContractLogo = dataURL;
            })
            .catch(error => {
                console.error('Error caching image:', error);
                this.cachedContractLogo = this.contractLogo; // Fallback to original URL if caching fails
            })
            .finally(() => {
                this.loadedContractLogo = true;
            });
        }
    },
    watch: {
        contractLogo(url) {
            this.cacheImage(url);
        }
    },
    mounted() {
        this.cacheImage(this.contractLogo);
    }
}
</script>
